import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type { NumberFormatterOptions } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  BalanceSheetIncomeBalanceType,
  BcDimensionBreakdownBatch,
  BuildBalanceSheetReportRequest,
} from "../../../../../../api/types/svTypes.ts";
import { DimensionNames } from "../../../sv/utilities/DimensionNames.ts";
import { formatToISODate } from "../../../utils/dateUtils.ts";
import type { BalanceSheetEntityFilter } from "./balanceSheetEntityFilter.ts";
import type { BalanceSheetPeriodRange, BalanceSheetPriorPeriodType } from "./balanceSheetPeriod.ts";
import { getBalanceSheetPriorPeriodSettingsFromSelection } from "./balanceSheetPeriod.ts";

const STANDARD_BALANCE_SHEET_REPORT_ID = "standard-balance-sheet";
const STANDARD_INCOME_STATEMENT_REPORT_ID = "standard-income-statement";

export interface BuildBalanceSheetPeriodSettingsInput {
  priorPeriodType: BalanceSheetPriorPeriodType;
  priorPeriodCount: number;
}

export const buildBalanceSheetRequest = (
  sessionId: string,
  cacheSessionId: string,
  reportId: string,
  incomeBalanceType: BalanceSheetIncomeBalanceType,
  accountingBook: string | null = null,
  periodRange: BalanceSheetPeriodRange,
  periodSettings: BuildBalanceSheetPeriodSettingsInput,
  numberFormat: NumberFormatterOptions | undefined,
  isQueryLoggingActive: boolean,
  entityFilter?: BalanceSheetEntityFilter,
  breakdownBatches?: BcDimensionBreakdownBatch[]
): BuildBalanceSheetReportRequest => {
  const conditions = [
    {
      guid: generateGuid(),
      filter: {
        dimensionName: DimensionNames.PostingDate,
        equalityType: ConditionEqualityType.Between,
        values: [formatToISODate(periodRange.from), formatToISODate(periodRange.to)],
      },
    },
  ];

  if (entityFilter) {
    conditions.push({
      guid: generateGuid(),
      filter: {
        dimensionName: entityFilter.dimensionName,
        equalityType: ConditionEqualityType.Equal,
        values: [entityFilter.entityNo],
      },
    });
  }

  const hasBreakdown = breakdownBatches && breakdownBatches.length > 0;

  return {
    reportId,
    sessionId,
    cacheSessionId,
    withDrillDown: true,
    useQueryLogging: isQueryLoggingActive,
    incomeBalanceType,
    accountingBook,
    conditions,
    settings: {
      ...(hasBreakdown
        ? {}
        : getBalanceSheetPriorPeriodSettingsFromSelection(
            periodSettings.priorPeriodType,
            periodSettings.priorPeriodCount
          )),
      ...(numberFormat ? { numberFormat: { ...numberFormat } } : {}),
    },
    ...(hasBreakdown ? { breakdownBatches } : {}),
  };
};

export { STANDARD_BALANCE_SHEET_REPORT_ID, STANDARD_INCOME_STATEMENT_REPORT_ID };
